import type { ComicSource, SourceAuth } from '../sources/types.ts';

export interface CookieSaveValidationOptions {
  anonymousMode: boolean;
  skipSourceValidation: boolean;
}

export interface CookieSaveValidationResult {
  /** Auth to persist; may include cookies added by `source.prepareAuth`. */
  auth: SourceAuth;
  username?: string;
  validated: boolean;
}

export async function validateCookieSave(
  source: ComicSource,
  pastedAuth: SourceAuth,
  opts: CookieSaveValidationOptions,
): Promise<CookieSaveValidationResult> {
  let auth = pastedAuth;
  if (source.prepareAuth && !opts.anonymousMode) {
    try {
      auth = await source.prepareAuth(pastedAuth);
    } catch (err) {
      // "Save without validating" exists for blocked proxy paths; keep the
      // pasted cookies rather than failing on the same block here.
      if (!opts.skipSourceValidation) throw err;
    }
  }

  if (opts.skipSourceValidation || !source.validateAuth) {
    return { auth, validated: false };
  }

  const result = await source.validateAuth(
    auth,
    opts.anonymousMode ? { anonymous: true } : undefined,
  );
  if (result.ok) {
    return result.username !== undefined
      ? { auth, username: result.username, validated: true }
      : { auth, validated: true };
  }
  if (opts.anonymousMode) {
    throw new Error(
      `Cookies still got a Cloudflare challenge for ${source.name}. Refresh cf_clearance and __cf_bm, then paste them again.`,
    );
  }
  const reason = result.reason?.trim();
  throw new Error(
    reason || `Cookies did not validate for ${source.name}. Refresh them and try again.`,
  );
}

export interface FormLoginRequest {
  username: string;
  password: string;
}

export interface FormLoginSaveResult {
  username?: string;
}

export type SaveSourceAuth = (
  sourceId: string,
  auth: SourceAuth,
  token: string,
  opts?: { username?: string },
) => Promise<void>;

export async function loginWithFormAndSave(
  source: ComicSource,
  request: FormLoginRequest,
  token: string,
  saveAuth: SaveSourceAuth,
): Promise<FormLoginSaveResult> {
  if (!source.loginWithForm) {
    throw new Error(`${source.name} does not support username/password login.`);
  }

  const result = await source.loginWithForm(request);
  if (!result.ok || !result.auth) {
    throw new Error(result.reason?.trim() || `Login failed for ${source.name}.`);
  }

  await saveAuth(source.id, result.auth, token, { username: result.username });
  return result.username !== undefined ? { username: result.username } : {};
}
